import { afterNextRender, Component, effect, ElementRef, inject, Injector, OnInit, QueryList, signal, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DocumentTypeFilter, DocumentTypeService } from '../../../../../services/document-type.service';
import { IUser } from '../../../../../../@core/models/model';
import { IDuplicateClusterResponse, IQnaPair, QnAPairService } from '../../../../../services/qna-pair.service';
import { IFormAnswer, IFormQuestion } from '../../../../../models/contract-management.model';
import { FormatTextPipe } from '../../../../../../@shared/pipes/formated-text.pipe';
import { ToastService } from '../../../../../../@core/services/toast.service';
import { DynamicComponentRenderer } from '../../../../../../@shared/directives/dynamic-component-renderer.directive';
import _ from 'lodash';
import { AccordionModule } from 'primeng/accordion';
import { Textarea } from 'primeng/textarea';
import { ButtonComponent } from "../../../../../../@core/components/ui/button/button.component";
import { DialogComponent } from "../../../../../../@core/components/ui/dialog/dialog.component";
import { DialogService } from "../../../../../../@core/services/dialog.service";
import { CheckboxComponent } from "../../../../../../@core/components/ui/checkbox/checkbox.component";
import { SelectComponent, SelectOption, SelectConfig } from "../../../../../../@core/components/ui/select";
import { UserNameDisplayComponent } from "../../../../../../@shared/components/user-name-display/user-name-display.component";
import { Tooltip, TooltipModule } from "primeng/tooltip";

@Component({
    selector: 'app-duplicate-qna',
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        CheckboxModule,
        SelectModule,
        ProgressSpinnerModule,
        FormatTextPipe,
        DynamicComponentRenderer,
        AccordionModule,
        Textarea,
        ButtonComponent,
        DialogComponent,
        CheckboxComponent,
        SelectComponent,
        UserNameDisplayComponent,
        TooltipModule
    ],
    standalone: true,
    templateUrl: './duplicate-qna.component.html',
    styleUrl: './duplicate-qna.component.scss'
})
export class DuplicateQnaComponent implements OnInit {
    selectedQuestions: string[] = [];
    isSelectAll: boolean = false;
    selectedDocumentType: string = '';
    documentTypeOptions: SelectOption[] = [];
    documentTypeConfig: SelectConfig = {
        placeholder: 'Select Document Type',
        searchable: true,
        clearable: false,
        styleClass: 'font-semibold rounded-lg text-black border border-gray-200 w-full',
        appendTo: 'body'
    };
    duplicateClusterLoading: boolean = false;
    duplicateQnaPairs: (IQnaPair & { reviewers: Partial<IUser>[] })[] = [];
    expandCell: boolean = false;
    expandedCellContent: string = '';
    expandedCellType: string = '';
    duplicateClusters: IDuplicateClusterResponse[] = []
    duplicateClusterOptions: { clusterId: string, title: string, count: number }[] = []
    selectedDuplicateCluster = signal<{ clusterId: string, title: string } | undefined>(undefined);
    showBulkEditDialog: boolean = false
    editingPairs: (IQnaPair & { question?: string, answer?: string, reviewer?: string })[] = [];
    originalPairs: (IQnaPair & { question?: string, answer?: string, reviewer?: string })[] = [];
    currentEditIndex: number = 0;
    savedPairs: Set<number> = new Set();
    @ViewChildren('qnaCard') qnaCards!: QueryList<ElementRef>;
    private readonly injector = inject(Injector);
    isSaving: boolean = false;
    showMergeDialog: boolean = false;
    mergePairs: IQnaPair[] = []
    mergePairsSelected: Set<string> = new Set()
    mergeSuggestionLoading: boolean = false
    suggestion: { question: string, answer: string } = { question: '', answer: '' }
    selectedMergeQuestionsChanged: boolean = false
    savingMergeAnswer: boolean = false

    constructor(
        private documentTypeService: DocumentTypeService,
        private qnaPairService: QnAPairService,
        private toast: ToastService,
        private dialogService: DialogService
    ) {
        effect(() => {
            const selected = this.selectedDuplicateCluster();
            const cluster = this.duplicateClusters.find(item => item._id == selected?.clusterId)
            this.duplicateQnaPairs = cluster?.questions.map(item => item.linkedQnAPair) ?? []
            this.selectedQuestions = []
            this.isSelectAll = false
        })
    }

    ngOnInit() {
        this.getDocTypesWithForm();
    }

    getDocTypesWithForm() {
        this.documentTypeService.getDocTypes([DocumentTypeFilter.FORM_RELATED]).subscribe(res => {
            if (res && res.success) {
                this.documentTypeOptions = res.data.map((doc) => {
                    return {
                        label: doc.name.charAt(0).toUpperCase() + doc.name.slice(1).toLowerCase(),
                        value: doc._id
                    }
                });
                this.selectedDocumentType = this.documentTypeOptions?.[0]?.['value'] ?? '';
                this.getDuplicateClusters();
            }
        })
    }

    getDuplicateClusters() {
        if (!this.selectedDocumentType) return
        this.qnaPairService.getDuplicateQuestions(this.selectedDocumentType).subscribe((res) => {
            if (res.success) {
                this.duplicateClusterOptions = []
                this.duplicateClusters = res.data
                this.duplicateClusters.forEach(cluster => {
                    const q = cluster.questions?.[0]
                    if (!q) return;
                    this.duplicateClusterOptions.push({ clusterId: cluster._id, title: (q.linkedQnAPair?.question as IFormQuestion)?.title, count: cluster.questions.length })
                })
                this.selectedDuplicateCluster.set(this.duplicateClusterOptions?.[0])
            }
        });
    }

    selectDuplicateCluster(cluster: any) {
        this.selectedDuplicateCluster.set(cluster)
    }

    selectDocumentType(documentType: string): void {
        this.selectedDocumentType = documentType;
        this.selectedQuestions = [];
        this.isSelectAll = false;
        this.getDuplicateClusters();
    }

    toggleSelectAll(event: any): void {
        if (event.checked) {
            this.selectedQuestions = this.duplicateQnaPairs.map(item => (item.question as IFormQuestion)._id);
            this.isSelectAll = true;
        } else {
            this.selectedQuestions = [];
            this.isSelectAll = false;
        }
    }

    get selectAllChecked(): boolean {
        return this.isSelectAll || (this.selectedQuestions.length > 0 && this.selectedQuestions.length === this.duplicateQnaPairs.length);
    }

    isSelected(qna: IQnaPair): boolean {
        return this.selectedQuestions.includes((qna.question as IFormQuestion)?._id);
    }

    getQuestionTitle(qna: IQnaPair): string {
        if (typeof qna.question === 'string') {
            return qna.question;
        }
        return (qna.question as IFormQuestion)?.title || '';
    }

    getAnswerValue(qna: IQnaPair): string {
        if (typeof qna.answer === 'string') {
            return qna.answer;
        }
        return (qna.answer as IFormAnswer)?.value || '';
    }

    getTimesUsed(qna: IQnaPair): string {
        return qna.timesUsed.toString();
    }

    getSourceType(qna: IQnaPair): string {
        return qna.sourceType?.charAt(0)?.toUpperCase() + qna.sourceType?.slice(1).toLowerCase() || '';
    }

    getAuthorName(qna: IQnaPair): string {
        if (typeof qna.author === 'string') {
            return qna.author;
        }
        return (qna.author as IUser)?.fullName || '';
    }

    toggleRowSelection(item: IQnaPair, event: any): void {
        const questionId = (item.question as IFormQuestion)._id;
        if (event.checked) {
            if (!this.selectedQuestions.includes(questionId)) {
                this.selectedQuestions.push(questionId);
            }
            if (this.selectedQuestions.length === this.duplicateQnaPairs.length) {
                this.isSelectAll = true;
            } else {
                this.isSelectAll = false;
            }
        } else {
            this.selectedQuestions = this.selectedQuestions.filter(id => id !== questionId);
            this.isSelectAll = false;
        }
    }

    acceptQuestion(event: Event) {
        this.dialogService.confirm({
            title: 'Accept as Final Answer?',
            description: 'Are you sure you want to save this answer and discard others?',
            confirmText: 'Yes',
            cancelText: 'No',
        }).subscribe(confirmed => {
            if (confirmed) {
                const selectedQuestion = this.selectedQuestions[0];
                this.qnaPairService.acceptQuestionInCluster(selectedQuestion).subscribe((res) => {
                    if (res.success) {
                        this.getDuplicateClusters();
                        this.toast.success('Question accepted for duplicates successfully.');
                    } else {
                        this.toast.error('Question accepted for duplicates failed.');
                    }
                });
            }
        });
    }

    deleteQuestion(event: Event) {
        this.dialogService.confirm({
            title: 'Remove Answer?',
            description: 'This action will remove the answer entirely, do you want to proceed with it?',
            confirmText: 'Yes',
            cancelText: 'No',
            confirmVariant: 'destructive',
        }).subscribe(confirmed => {
            if (confirmed) {
                this.qnaPairService.deleteQuestionInCluster(this.selectedQuestions).subscribe((res) => {
                    if (res.success) {
                        this.getDuplicateClusters();
                        this.toast.success('Questions deleted for duplicates successfully.');
                    } else {
                        this.toast.error('Questions deleted for duplicates failed.');
                    }
                });
            }
        });
    }

    markNotDuplicateQuestion(event: Event) {
        this.dialogService.confirm({
            title: 'Mark as Not Duplicate?',
            description: 'Are you sure this question is not a duplicate?',
            confirmText: 'Yes',
            cancelText: 'No',
        }).subscribe(confirmed => {
            if (confirmed) {
                this.qnaPairService.updateQuestionsAsNotDuplicate(this.selectedQuestions).subscribe((res) => {
                    if (res.success) {
                        this.getDuplicateClusters();
                        this.toast.success('Questions marked as not duplicate successfully.');
                    } else {
                        this.toast.error('Questions marked as not duplicate failed.');
                    }
                });
            }
        });
    }

    openModel(answer: string, type: string) {
        this.expandCell = !this.expandCell;
        this.expandedCellContent = answer;
        this.expandedCellType = type;
        if (!this.expandCell) {
            this.expandedCellContent = '';
        }
    }

    getRowClass(pair: IQnaPair) {
        const questionId = (pair.question as IFormQuestion)?._id;
        return {
            'selected-row': this.selectedQuestions.includes(questionId),
            'bg-arena-orange-100': this.selectedQuestions.includes(questionId),
            'hover:bg-arena-orange-100': this.selectedQuestions.includes(questionId),
        };
    }

    getLastEdited(qna: IQnaPair): string {
        return (qna as any).lastEdited || '-';
    }

    selectQnaPair(index: number) {
        this.currentEditIndex = index;
        this.scrollToActiveCard();
    }

    openBulkEditDialog() {
        const selectedItemsSet = new Set(this.selectedQuestions)
        const selectedQnaPairs = this.duplicateQnaPairs.filter(item => selectedItemsSet.has((item.question as IFormQuestion)._id))
        this.editingPairs = selectedQnaPairs.map(pair => ({
            ...pair,
            question: (pair.question as IFormQuestion).title,
            answer: (pair.answer as IFormAnswer).value,
            reviewer: pair.reviewers.length > 0 ? pair.reviewers[0]._id : undefined
        }));
        this.originalPairs = this.editingPairs.map(pair => ({ ...pair }));
        if (this.editingPairs.length === 0) {
            this.toast.error("Please select at least one QnA pair to edit");
            return;
        }
        this.showBulkEditDialog = true
    }

    closeBulkEditDialog() {
        this.showBulkEditDialog = false
        this.editingPairs = []
        this.originalPairs = []
        this.savedPairs = new Set()
        this.selectedQuestions = []
        this.isSelectAll = false
        this.currentEditIndex = 0
    }

    savePair(index: number) {
        if (
            this.originalPairs[index].question === this.editingPairs[index].question &&
            this.originalPairs[index].answer === this.editingPairs[index].answer
        ) {
            this.savedPairs.delete(index);
        } else {
            this.savedPairs.add(index);
        }
    }

    saveIncompletePairs() {
        this.dialogService.confirm({
            title: 'Save Incomplete Edit?',
            description: 'You can save progress, but incomplete data may affect performance. Continue?',
            confirmText: 'Yes, Continue',
            cancelText: 'Cancel',
        }).subscribe(confirmed => {
            if (confirmed) {
                this.onSaveBulkEdit(false);
            }
        });
    }

    onSaveBulkEdit(checkComplete: boolean = true) {
        const modifiedPairs = Array.from(this.savedPairs).map(index => {
            const pair = this.editingPairs[index];
            return {
                id: pair._id,
                question: pair.question || '',
                answer: pair.answer || '',
                reviewer: pair.reviewer || ''
            };
        });
        if (checkComplete && modifiedPairs.length !== this.editingPairs.length) {
            this.saveIncompletePairs()
            return;
        }
        this.isSaving = true;
        this.qnaPairService.bulkUpdateQnaPairs(modifiedPairs).subscribe({
            next: (res) => {
                this.isSaving = false;
                if (res.success) {
                    this.toast.success(`${modifiedPairs.length} QnA pairs updated successfully`);
                    this.showBulkEditDialog = false;
                    this.getDuplicateClusters()
                    this.closeBulkEditDialog();
                }
            },
            error: () => {
                this.isSaving = false;
                this.toast.error("Failed to update QnA pairs");
            }
        });
    }

    navigatePrevious() {
        if (this.currentEditIndex > 0) {
            this.currentEditIndex--;
        }
        this.scrollToActiveCard();
    }

    navigateNext() {
        if (this.currentEditIndex < this.editingPairs.length - 1) {
            this.currentEditIndex++;
        }
        this.scrollToActiveCard();
    }

    scrollToActiveCard() {
        afterNextRender(() => {
            const cards = this.qnaCards.toArray();
            cards[this.currentEditIndex]?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, { injector: this.injector });
    }

    openMergeDialog() {
        const selectedItemsSet = new Set(this.selectedQuestions)
        this.mergePairs = this.duplicateQnaPairs.filter(item => selectedItemsSet.has((item.question as IFormQuestion)._id))
        if (this.mergePairs.length < 2) {
            this.toast.error("Please select at least two Answers to merge");
            this.closeMergeDialog()
            return;
        }
        this.mergePairs.forEach(item => { this.mergePairsSelected.add(item._id) })
        this.showMergeDialog = true
        this.getMergeSmartSuggestion()
    }

    getMergeSmartSuggestion() {
        this.mergeSuggestionLoading = true
        const selectedMergedPairs = this.mergePairs.filter(item => this.mergePairsSelected.has(item._id))
        const questionIds = selectedMergedPairs.map(item => (item.question as IFormQuestion)._id)
        const answerIds = selectedMergedPairs.map(item => (item.answer as IFormAnswer)._id)
        this.qnaPairService.getSmartSuggestions(questionIds, answerIds).subscribe((res) => {
            if (res.success) {
                this.suggestion = res.data
            }
            this.selectedMergeQuestionsChanged = false
            this.mergeSuggestionLoading = false
        })
    }

    getMergeQuestion(pair: IQnaPair) {
        return (pair.question as IFormQuestion).title
    }

    getMergeAnswer(pair: IQnaPair) {
        return (pair.answer as IFormAnswer).value
    }

    selectMergeAnswer(qnaPairId: string) {
        this.selectedMergeQuestionsChanged = true
        if (this.mergePairsSelected.has(qnaPairId))
            this.mergePairsSelected.delete(qnaPairId)
        else
            this.mergePairsSelected.add(qnaPairId)
    }

    closeMergeDialog() {
        this.showMergeDialog = false
        this.isSelectAll = false
        this.selectedQuestions = []
        this.editingPairs = []
        this.mergePairs = []
        this.mergePairsSelected = new Set()
        this.mergeSuggestionLoading = false
        this.suggestion = { question: '', answer: '' }
    }

    saveMergeAnswerPrompt() {
        this.dialogService.confirm({
            title: 'Accept & Save?',
            description: 'This action will discard all the other answers and will get replaced by the merged answer. Do you wish to continue?',
            confirmText: 'Yes, Continue',
            cancelText: 'Cancel',
        }).subscribe(confirmed => {
            if (confirmed) {
                this.saveMergeAnswer(true);
            }
        });
    }

    saveMergeAnswer(accept: boolean = false) {
        this.savingMergeAnswer = true
        let qnaPairIds = []
        if (accept) {
            qnaPairIds = this.mergePairs.map(item => item._id)
        }
        else {
            qnaPairIds = this.mergePairs.filter(item => this.mergePairsSelected.has(item._id)).map(item => item._id)
        }
        this.qnaPairService.createMergedQnaPair({ question: this.suggestion.question, answer: this.suggestion.answer, documentType: this.selectedDocumentType, qnaPairIds, accept }).subscribe((res) => {
            if (res.success) {
                this.closeMergeDialog()
                this.toast.success('Answers merged successfully');
                this.getDuplicateClusters()
            }
            this.savingMergeAnswer = false
        })
    }

    get isPreviousDisabled() {
        return this.currentEditIndex === 0
    }
    get isNextDisabled() {
        return this.currentEditIndex === this.editingPairs.length - 1
    }
    get isSaveDisabled() {
        return this.savedPairs.size === 0
    }

    copyAnswerToClipboard(qna: IQnaPair) {
        navigator.clipboard.writeText(this.getAnswerValue(qna)).then(() => {
            this.toast.success('Answer copied to clipboard');
        }).catch((err) => {
            this.toast.error('Failed to copy answer to clipboard');
        });
    }

} 
